<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Alert from '../components/Alert.svelte';
  import Button from '../components/Button.svelte';
  import DataTable from '../components/DataTable.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getConfig, runQuery, saveQuery, deleteQuery } from '../lib/api.js';
  import { queryDraft } from '../lib/queryDraft.svelte.js';
  import { go } from '../lib/nav.svelte.js';
  import { NS } from '../../shared/config.js';
  import { HELP } from '../lib/help.js';
  import CompareBuilder from '../components/CompareBuilder.svelte';
  import DeleteQuery from '../components/DeleteQuery.svelte';
  import CommonCubes from '../components/CommonCubes.svelte';
  import Facets from '../components/Facets.svelte';
  import Async from '../components/Async.svelte';
  import SegmentedControl from '../components/SegmentedControl.svelte';
  import { allowedViews, VIEWS } from '../../../lib/value_schemes.js';
  import { getCubes, getCatalog, getDatasets } from '../lib/api.js';

  // What the comparison builder can reach. The catalog read is only for the
  // partner's declared measure — /api/cubes classifies their cells against ours
  // by design and so never learns theirs — and a partner we cannot read is not
  // a reason to lose the builder, hence the null fallback rather than a reject.
  const comparable = Promise.all([getCubes(), getCatalog().catch(() => null)])
    .then(([cubes, catalog]) => ({ cubes, catalog }));
  // Every catalog term's value scheme, for the term a hand-written query is
  // about and the views it may open as.
  let schemes = $state({});
  comparable.then(({ cubes }) => { schemes = cubes.schemes ?? {}; }).catch(() => {});

  // The named queries the Pod holds, for the builder's id-collision warning.
  let named = $state([]);
  async function loadNamed() {
    try { named = (await getConfig('queries')).queries ?? []; }
    catch { named = []; }
  }
  loadNamed();

  // Custom-query editor: a starter query, or a saved one being edited.
  const starter = `PREFIX core:    <${NS.core}>
PREFIX dcterms: <${NS.dcterms}>
PREFIX qb:      <${NS.qb}>
SELECT ?publisher ?period (COUNT(?o) AS ?cells) WHERE {
  ?o a qb:Observation ; dcterms:publisher ?publisher ; core:period ?period .
} GROUP BY ?publisher ?period ORDER BY ?publisher ?period`;

  let custom = $state(starter);

  let result = $state(null);
  let error = $state(null);
  let running = $state(false);

  async function run() {
    running = true; error = null; result = null;
    try {
      result = await runQuery(custom);
    } catch (e) {
      error = e.message;
    } finally {
      running = false;
    }
  }

  // Save-as-named-query. The label becomes the panel title; its dataset groups
  // it on Analysis. The backend validates (runs the SPARQL once) and enforces
  // the analyst role — a save failure surfaces its message here.
  let saveLabel = $state('');
  let saveDataset = $state('');
  let datasets = $state([]);
  getDatasets().then((r) => { datasets = r.datasets; }).catch(() => {});
  let saveDesc = $state('');
  // What the query's ?value is about, and what it opens as: the same choice the
  // builder makes, made by hand for a query the builder cannot write.
  let saveTerm = $state('');
  let chosenView = $state(null);
  const saveViews = $derived(allowedViews(schemes[saveTerm] ?? null));
  const saveView = $derived(saveViews.includes(chosenView) ? chosenView : saveViews.at(-1));
  // The saved query being edited: saving replaces it, under whatever name.
  let editingId = $state(null);
  let saveUnit = $state(null); // kept as the query had it
  let saveTerms = $state(null); // likewise: the questions of one grouped by question
  let saveError = $state(null);
  let saved = $state(null);
  let saving = $state(false);

  async function save() {
    saving = true; saveError = null; saved = null;
    try {
      const entry = await saveQuery({
        ...(editingId ? { id: editingId } : {}),
        label: saveLabel, dataset: saveDataset || null, description: saveDesc || null, sparql: custom,
        ...(saveUnit ? { unit: saveUnit } : {}),
        ...(saveTerm ? { term: saveTerm, view: saveView } : {}),
        ...(saveTerm && saveTerms?.[0] === saveTerm ? { terms: saveTerms } : {}),
      });
      saved = entry;
      editingId = entry.id;
      await loadNamed();
    } catch (e) {
      saveError = e.message;
    } finally {
      saving = false;
    }
  }

  // Edit a saved query where it was made: a builder comparison (it has its
  // sides) in the builder, anything else here as SPARQL.
  let editingComparison = $state(null);
  function edit(q) {
    if (q.sides) {
      editingComparison = { ...q };
      facet = 'comparisons';
    } else {
      editAsSparql(q);
    }
    window.scrollTo?.(0, 0);
  }
  function editAsSparql(q) {
    custom = q.sparql;
    saveLabel = q.label ?? ''; saveDataset = q.dataset ?? ''; saveDesc = q.description ?? '';
    saveTerm = q.term ?? ''; chosenView = q.view ?? null;
    editingId = q.id ?? null;
    saveUnit = q.unit ?? null;
    saveTerms = q.terms ?? null;
    result = null; error = null; saved = null;
    facet = 'custom';
  }
  function startNew() {
    editingId = null; saveUnit = null; saveTerms = null;
    saveLabel = ''; saveDataset = ''; saveDesc = ''; saveTerm = ''; chosenView = null;
  }
  // Anything changed after a save brings the form back.
  $effect(() => { custom; saveLabel; saveDataset; saveDesc; saveTerm; saveView; saved = null; });

  // Deleting the saved query being edited, from either editor. Its panel is
  // gone from Comparisons, which is where the list of saved queries is.
  async function remove(id) {
    await deleteQuery(id);
    go('analysis');
  }

  // Two ways to write the same comparison: the builder, or SPARQL by hand.
  // Facets rather than one long screen, and the panels are hidden rather than
  // unmounted — switching away from a half-built comparison and back should
  // not have cost you the comparison, and the "Edit as custom query" handoff
  // crosses between them. (A missing cube is built from Cubes → Add new.)
  // Common cubes saves ready-made ones: every cube both sides publish
  // that no comparison covers yet.
  const FACETS = [
    { id: 'comparisons', label: 'Builder' },
    { id: 'custom', label: 'Custom query' },
    { id: 'common', label: 'Common cubes' },
  ];
  let facet = $state('comparisons');

  // The handoff out of the comparison builder has to take the analyst with it,
  // or the compiled query lands in a textarea on a facet they cannot see. From
  // here on it is a hand-written query: saving it drops the sides.
  const handOff = editAsSparql;

  // Opened by Edit on a Comparisons panel, or by Add new.
  const editing = $derived(facet === 'custom' ? !!editingId : facet === 'comparisons' && !!editingComparison);

  // Edit pressed on Comparisons.
  if (queryDraft.entry) {
    edit(queryDraft.entry);
    queryDraft.entry = null;
  }
</script>

<HelpDrawer {...HELP.queries} />

<h1 class="wf2-title wf2-title-level-1">{editing ? 'Edit comparison' : 'New comparison'}</h1>
<p class="wf2-subtitle">Build it from two cubes, write the query by hand, or add the cubes you both publish — the answers appear on Comparisons</p>

<Facets facets={FACETS} active={facet} onselect={(id) => (facet = id)} />

<!-- Hidden, not unmounted: each facet holds a draft, and switching between them
     is a normal part of building one query. -->
<div hidden={facet !== 'comparisons'}>
  <Section>
    <Async promise={comparable} loading="Reading which cubes a query can reach…">
      {#snippet children({ cubes, catalog })}
        <CompareBuilder
          {cubes} {catalog} existing={named}
          onrun={(sparql) => runQuery(sparql)}
          onsave={async (entry) => { const saved = await saveQuery(entry); await loadNamed(); return saved; }}
          onedit={handOff} ondelete={remove} editing={editingComparison} />
      {/snippet}
    </Async>
  </Section>
</div>

<div hidden={facet !== 'custom'}>
  <Section>
    <Panel>
      <p class="wf2-text">
        You write the question; the app decides what it is allowed to read. A
        query here can only ever reach the shared cubes this institution already
        has access to — never your private records, however it is written.
      </p>
      <textarea class="wf2-control mono" rows="9" bind:value={custom}></textarea>
      <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
        <Button variant="primary" onclick={run} disabled={running}>{running ? 'Running…' : 'Run query'}</Button>
      </div>

      {#if error}
        <Alert title="Query error" variant="danger"><p class="mono">{error}</p></Alert>
      {:else if result}
        {#if result.rows.length}
          <DataTable columns={result.columns} rows={result.rows.map((r) => result.columns.map((c) => r[c]))} />
        {:else}
          <p class="muted">No rows.</p>
        {/if}
      {/if}
    </Panel>
  </Section>

  <Section title="Save this query">
    <Panel>
      {#if editingId && !saved}
        <p class="wf2-caption">
          Editing a saved query: saving replaces it.
          <button type="button" class="link" onclick={startNew}>Save as a new one instead</button>
        </p>
      {/if}
      {#if saved}
        <Alert title="Saved to your Pod" variant="success">
          <p class="wf2-text">
            "{saved.label}" is saved. <a href="#/analysis">See it on Comparisons →</a>
          </p>
        </Alert>
      {:else}
        <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
          <input class="wf2-control" style="flex:2 1 200px" placeholder="Query name (e.g. Nationality distribution)" bind:value={saveLabel} />
          <select class="wf2-control" style="flex:1 1 160px" aria-label="Dataset" bind:value={saveDataset}>
            <option value="">(no dataset)</option>
            {#each datasets as d}<option value={d.id}>{d.label}</option>{/each}
          </select>
        </div>
        <textarea class="wf2-control" rows="3" style="margin-top: var(--space-2)" placeholder="Description (optional)" bind:value={saveDesc}></textarea>
        <!-- A term with a value scheme puts ?value in the scheme's order, with
             its labels, and allows the views beyond Separate. -->
        <div class="wf2-row" style="gap:12px; flex-wrap:wrap; align-items:center; margin-top: var(--space-2)">
          <label class="wf2-field" style="flex:0 1 320px">The values are
            <select class="wf2-control mono" bind:value={saveTerm}>
              <option value="">(no term: largest first)</option>
              {#each Object.keys(schemes) as id}
                <option value={id}>{id}</option>
              {/each}
            </select>
          </label>
          {#if saveViews.length > 1}
            <SegmentedControl label="Opens as" options={saveViews.map((id) => ({ id, label: VIEWS[id] }))}
              selected={saveView} onselect={(id) => (chosenView = id)} />
          {/if}
        </div>
        <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
          <Button variant="primary" onclick={save} disabled={saving || !saveLabel}>{saving ? 'Saving…' : editingId ? 'Save changes' : 'Save query'}</Button>
        </div>
        {#if saveError}
          <Alert title="Could not save" variant="danger"><p class="mono">{saveError}</p></Alert>
        {/if}
      {/if}
    </Panel>
  </Section>

  {#if editingId}<DeleteQuery label={saveLabel} ondelete={() => remove(editingId)} />{/if}
</div>

<div hidden={facet !== 'common'}>
  <CommonCubes onadded={loadNamed} />
</div>

<style>
  .link { font: inherit; background: none; border: 0; padding: 0; color: var(--accent); text-decoration: underline; cursor: pointer; }
</style>
